import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Script } from 'gatsby';
import { GlobalStyles } from '@utils/GlobalStyles';
import styled from 'styled-components';
import { chakra, ChakraProvider, useBreakpointValue } from '@chakra-ui/react';
import Head from '@rsc/ui-components/Head';
import Header from '@rsc/ui-components/dept/layout/Header';
import Cart from '@rsc/ui-components/dept/organisms/Cart';
import CartToast from '@rsc/ui-components/dept/organisms/Cart/CartToast';
import { InitInsights } from '@rsc/utils';
import { CartContext } from '@rsc/contexts/CartContext';
import { NonCommerceInfoProvider } from '@rsc/contexts/NonCommerceInfoContext';
import { BazaarVoiceProvider } from '@rsc/contexts';
import styles from '@utils/styles';
import Footer from '@rsc/ui-components/dept/layout/Footer';
import { MavenoidChatButton } from '@rsc/ui-components/openChatWidget';
import useSiteSettingsContext from '@components/hooks/useSiteSettingsContext';
import useAllCollections from '@components/hooks/useAllCollections';
import { showBazaarVoice } from '@util/helpers';
import { usePetsafeInternationalContext } from '@util/static-translations';
import { localeToKlaviyoPublicMap } from '@rsc/utils/klaviyo';
import { GLOBAL_LOCALES } from '@rsc/gatsby-localization-helpers';
import ChakraTheme from '../../@chakra-ui/gatsby-plugin/theme';
import PDPTheme from '../../theme/PDPTheme';

const CartWarning = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: ${styles.colors.gray[500]};
  padding: 0.5rem;
  font-size: 1rem;
`;

const isBrowser = () => typeof window !== 'undefined';

if (process.env.GATSBY_SANITY_PREVIEW_DEPLOY === 'true') {
  import('../../styles/web-preview.css');
}

function Layout({
  children,
  headTagData,
  isPDP,
  isNonCommerce,
  scriptsData,
  linkHreflangValues,
}) {
  const { cartError } = useContext(CartContext);
  const { title, description, keywords, meta, includeBVScript, enableKlaviyo } =
    headTagData;
  const { locale } = usePetsafeInternationalContext();

  InitInsights();

  const allCollections = useAllCollections();
  const { siteSettings } = useSiteSettingsContext();
  const {
    notificationBannerCarousel,
    footerCustomColumns,
    footerCopyright,
    footerLegal,
    socialMedia,
    newsletter,
    shipping,
    mainMenu,
  } = siteSettings;

  const links = [];
  if (headTagData.canonicalUrl) {
    links.push(
      <link href={headTagData.canonicalUrl} key="canonical" rel="canonical" />
    );
  } else if (isBrowser()) {
    links.push(
      <link href={window.location.href} key="canonical" rel="canonical" />
    );
  }

  const carouselHeight = useBreakpointValue({
    base: 80,
    md: 40,
  });

  return (
    <>
      <ChakraProvider theme={!isPDP ? ChakraTheme : PDPTheme}>
        <BazaarVoiceProvider showBazaarVoiceFunc={showBazaarVoice}>
          <NonCommerceInfoProvider isNonCommerce={isNonCommerce}>
            <Head
              title={title}
              description={description}
              keywords={keywords}
              meta={meta}
              noIndex={headTagData?.noIndex}
              noFollow={headTagData?.noFollow}
              scriptTags={scriptsData}
              linkTags={links}
              lang={locale}
              hreflangs={linkHreflangValues}
            >
              {locale === GLOBAL_LOCALES.English.id && (
                <script key="google-tag-manager" async>
                  {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${process.env.GATSBY_GTM_ID}');`}
                </script>
              )}
              {locale === GLOBAL_LOCALES.English.id && (
                <script type="text/javascript">
                  {`adroll_adv_id = "VXTXGIHXOZEF7MCRLXN3NZ";
                adroll_pix_id = "TADDESJSIVDCDBNU3LEUKT";
                adroll_version = "2.0";

                (function(w, d, e, o, a) {
                w.__adroll_loaded = true;
                w.adroll = w.adroll || [];
                w.adroll.f = [ 'setProperties', 'identify', 'track', 'identify_email', 'get_cookie' ];
                var roundtripUrl = "https://s.adroll.com/j/" + adroll_adv_id
                + "/roundtrip.js";
                for (a = 0; a < w.adroll.f.length; a++) {
                w.adroll[w.adroll.f[a]] = w.adroll[w.adroll.f[a]] || (function(n) {
                return function() {
                w.adroll.push([ n, arguments ])
                }
                })(w.adroll.f[a])
                }

                e = d.createElement('script');
                o = d.getElementsByTagName('script')[0];
                e.async = 1;
                e.src = roundtripUrl;
                o.parentNode.insertBefore(e, o);
              })(window, document);
                adroll.track("pageView");`}
                </script>
              )}
            </Head>
            <GlobalStyles />
            <Cart freeShippingThreshold={shipping} />
            <CartToast />
            {cartError && (
              <CartWarning>
                Purchases cannot be completed in the store right now. Please
                refresh or try again later.
              </CartWarning>
            )}

            <Header
              menuItems={mainMenu}
              notificationBanner={
                notificationBannerCarousel?.notificationBannerItems
              }
              ChatComponent={MavenoidChatButton}
              carouselHeight={carouselHeight}
              allCollections={allCollections}
            />
            <chakra.main
              id="main"
              pt={{
                base: `${carouselHeight + 120}px`,
                md: `${carouselHeight + 148}px`,
                lg: `${carouselHeight + 100}px`,
              }}
            >
              {children}
            </chakra.main>
            <Footer
              footerCustomColumns={footerCustomColumns}
              footerCopyright={footerCopyright}
              footerLegal={footerLegal}
              socialMedia={socialMedia}
              newsletter={newsletter}
            />
          </NonCommerceInfoProvider>
        </BazaarVoiceProvider>
      </ChakraProvider>
      {locale === GLOBAL_LOCALES.English.id && (
        <Script
          key="mavenoid"
          src="https://widget-hosts.mavenoid.com/custom-embedding-scripts/radio-systems-corp.js"
          onLoad={() => {
            window.mavenoid = window?.mavenoid || [];

            // hide mavenoid chat ui behind cookiepro banner
            const observer = new MutationObserver(
              (mutations, mutationInstance) => {
                const mavenoidComponent =
                  document.querySelector('mavenoid-assistant');
                if (mavenoidComponent) {
                  mavenoidComponent.style.zIndex = 1000;
                  mutationInstance.disconnect();
                }
              }
            );

            observer.observe(document, {
              childList: true,
              subtree: true,
            });
          }}
        />
      )}

      {process.env.GATSBY_COOKIEPRO_SITE_ID && [
        <Script
          async
          key="otSDKStub"
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          data-domain-script={process.env.GATSBY_COOKIEPRO_SITE_ID}
          data-document-language="true"
          data-culture={locale}
        />,
      ]}

      {enableKlaviyo &&
        localeToKlaviyoPublicMap(locale, process.env.GATSBY_KLAVIYO_BRAND)
          .publicKey && (
          <Script
            key="klaviyo"
            src={`https://static.klaviyo.com/onsite/js/${
              localeToKlaviyoPublicMap(locale, process.env.GATSBY_KLAVIYO_BRAND)
                .publicKey
            }/klaviyo.js`}
            async
            onLoad={() => {
              const observer = new MutationObserver(
                (_mutations, mutationInstance) => {
                  const klaviyoComponent =
                    document.querySelector('.needsclick');

                  if (klaviyoComponent) {
                    klaviyoComponent.style.zIndex = 15000;
                    mutationInstance.disconnect();
                  }
                }
              );

              observer.observe(document, {
                childList: true,
                subtree: true,
              });
            }}
          />
        )}

      {includeBVScript && showBazaarVoice(locale) && (
        <Script
          key="bazaarvoice"
          src={`https://apps.bazaarvoice.com/deployments/petsafe/main_site/${process.env.GATSBY_BAZAARVOICE_ENVIRONMENT}/en_US/bv.js`}
        />
      )}
    </>
  );
}

Layout.defaultProps = {
  isPDP: false,
  isNonCommerce: false,
  scriptsData: [],
  headTagData: {
    noIndex: false,
    noFollow: false,
  },
  linkHreflangValues: [],
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  headTagData: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    canonicalUrl: PropTypes.string,
    keywords: PropTypes.string,
    meta: PropTypes.arrayOf(PropTypes.shape({})),
    productSchema: PropTypes.string,
    includeBVScript: PropTypes.bool,
    enableKlaviyo: PropTypes.bool.isRequired,
    noIndex: PropTypes.bool,
    noFollow: PropTypes.bool,
  }),
  scriptsData: PropTypes.arrayOf(PropTypes.shape({})),
  isPDP: PropTypes.bool,
  isNonCommerce: PropTypes.bool,
  linkHreflangValues: PropTypes.arrayOf(PropTypes.shape({})),
};

export default Layout;
